import React, { useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Chip,
  Stack,
  useMediaQuery,
  Button,
  Divider,
  IconButton,
  Drawer,
  Avatar,
  Checkbox,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { 
  fetchMothersByPartner,
  setFilters,
  clearFilters,
  setPagination,
  setSelectedMothers,
  toggleMotherSelection,

  selectMothersList,
  selectPartnerInfo,
  selectMothersLoading,
  selectMothersError,
  selectMothersFilters,
  selectMothersPagination,
  selectSelectedMothers,
  selectFilteredMothers,
  selectPaginatedMothers,
  selectLocationOptions,
  selectChildrenOptions
} from '../../store/slices/mothersSlice';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import SchoolIcon from '@mui/icons-material/School';
import SavingsIcon from '@mui/icons-material/Savings';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import People from '@mui/icons-material/People';
import CheckCircle from '@mui/icons-material/CheckCircle';

const climateKeywords = [
  'climate', 'tree', 'green', 'environment', 'solar', 'eco', 'recycle', 'sustain', 'water', 'clean', 'energy', 'carbon', 'forest', 'agro', 'garden', 'compost', 'biodiversity', 'organic', 'nature', 'earth', 'renewable', 'conservation', 'farming', 'plant', 'waste', 'climate-smart', 'climate change', 'resilience', 'adaptation', 'mitigation', 'agroforestry'
];

const Mothers = React.memo(() => {
  const { partnerId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useAppDispatch();

  // Get state from Redux using optimized selectors
  const mothers = useAppSelector(selectMothersList);
  const partnerInfo = useAppSelector(selectPartnerInfo);
  const loading = useAppSelector(selectMothersLoading);
  const error = useAppSelector(selectMothersError);
  const filters = useAppSelector(selectMothersFilters);
  const pagination = useAppSelector(selectMothersPagination);
  const selected = useAppSelector(selectSelectedMothers);
  const filteredMothers = useAppSelector(selectFilteredMothers);
  const paginatedMothers = useAppSelector(selectPaginatedMothers);
  const locationOptions = useAppSelector(selectLocationOptions);
  const childrenOptions = useAppSelector(selectChildrenOptions);

  // Local state for UI components
  const [selectedMother, setSelectedMother] = React.useState(null);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [contributionOpen, setContributionOpen] = React.useState(false);
  const [contributionStep, setContributionStep] = React.useState(1);
  const [contributionData, setContributionData] = React.useState({
    mothers: [],
    amount: 30,
    frequency: 'monthly',
    paymentMethod: '',
    totalAmount: 0,
  });
  const [paymentMethods, setPaymentMethods] = React.useState([]);

  // Load mothers data on component mount
  useEffect(() => {
    if (partnerId) {
      console.log('Loading mothers for partner:', partnerId);
      dispatch(fetchMothersByPartner(partnerId));
    }
  }, [dispatch, partnerId]);

  // Retry mechanism for failed API calls
  const handleRetry = useCallback(() => {
    if (partnerId) {
      console.log('Retrying to fetch mothers for partner:', partnerId);
      dispatch(fetchMothersByPartner(partnerId));
    }
  }, [dispatch, partnerId]);

  // Load payment methods
  useEffect(() => {
    const savedMethods = localStorage.getItem(`paymentMethods_${partnerId}`);
    if (savedMethods) {
      setPaymentMethods(JSON.parse(savedMethods));
    }
  }, [partnerId]);

  // Memoized selection logic
  const isSelected = useCallback((id) => selected.indexOf(id) !== -1, [selected]);
  
  const handleSelectAllClick = useCallback((event) => {
    if (event.target.checked) {
      const newSelected = filteredMothers.map((n) => n.generated_id);
      dispatch(setSelectedMothers(newSelected));
      return;
    }
    dispatch(setSelectedMothers([]));
  }, [filteredMothers, dispatch]);
  
  const handleClick = useCallback((event, id) => {
    dispatch(toggleMotherSelection(id));
  }, [dispatch]);

  // Memoized drawer logic
  const handleOpenDrawer = useCallback((mother) => {
    setSelectedMother(mother);
    setDrawerOpen(true);
  }, []);
  
  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
    setSelectedMother(null);
  }, []);

  // Memoized contribution logic
  const handleContribute = useCallback((mothers) => {
    setContributionData({
      mothers: Array.isArray(mothers) ? mothers : [mothers],
      amount: 30,
      frequency: 'monthly',
      paymentMethod: paymentMethods.find(m => m.isDefault)?.id || '',
      totalAmount: 0,
    });
    setContributionStep(1);
    setContributionOpen(true);
  }, [paymentMethods]);

  const handleContributionClose = useCallback(() => {
    setContributionOpen(false);
    setContributionStep(1);
    setContributionData({
      mothers: [],
      amount: 30,
      frequency: 'monthly',
      paymentMethod: '',
      totalAmount: 0,
    });
  }, []);

  const calculateTotalAmount = useCallback(() => {
    const baseAmount = contributionData.amount * contributionData.mothers.length;
    return contributionData.frequency === 'quarterly' ? baseAmount * 3 : baseAmount;
  }, [contributionData.amount, contributionData.mothers.length, contributionData.frequency]);

  const handleContributionNext = useCallback(() => {
    if (contributionStep === 1) {
      setContributionData(prev => ({
        ...prev,
        totalAmount: calculateTotalAmount()
      }));
      setContributionStep(2);
    } else if (contributionStep === 2) {
      setContributionStep(3);
    }
  }, [contributionStep, calculateTotalAmount]);

  const handleContributionBack = useCallback(() => {
    setContributionStep(prev => Math.max(1, prev - 1));
  }, []);

  const handleContributionConfirm = useCallback(() => {
    console.log('Processing contribution:', contributionData);
    alert('Contribution processed successfully!');
    handleContributionClose();
  }, [contributionData, handleContributionClose]);

  // Memoized calculation functions
  const calculateFundingGap = useCallback((mother) => {
    const annualEducationCost = 1200; // USD per year
    const currentSavings = mother.savings || 0;
    const donorContributions = mother.donor_contributions || 0;
    const totalAvailable = currentSavings + donorContributions;
    return Math.max(0, annualEducationCost - totalAvailable);
  }, []);

  // Memoized filter handlers
  const handleLocationFilterChange = useCallback((value) => {
    dispatch(setFilters({ location: value }));
  }, [dispatch]);

  const handleChildrenFilterChange = useCallback((value) => {
    dispatch(setFilters({ children: value }));
  }, [dispatch]);

  const handleSavingsRangeChange = useCallback((value) => {
    dispatch(setFilters({ savingsRange: value }));
  }, [dispatch]);

  const handleClimateFilterChange = useCallback((value) => {
    dispatch(setFilters({ climateFilter: value }));
  }, [dispatch]);

  const handleResetFilters = useCallback(() => {
    dispatch(clearFilters());
  }, [dispatch]);

  // Memoized pagination handlers
  const handleChangePage = useCallback((event, newPage) => {
    dispatch(setPagination({ page: newPage }));
  }, [dispatch]);
  
  const handleChangeRowsPerPage = useCallback((event) => {
    dispatch(setPagination({ 
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0 
    }));
  }, [dispatch]);

  // Memoized stat cards data
  const statCardsData = useMemo(() => [
    {
      title: 'Total Mothers',
      value: partnerInfo?.mother_count || 0,
      icon: <People sx={{ fontSize: 40, opacity: 0.8 }} />,
      color: 'linear-gradient(135deg, #f92955 0%, #d41e3f 100%)',
      description: `Registered with ${partnerInfo?.partner_name}`
    },
    {
      title: 'Compliant Mothers',
      value: Math.floor((partnerInfo?.mother_count || 0) * 0.85),
      icon: <CheckCircle sx={{ fontSize: 40, opacity: 0.8 }} />,
      color: 'linear-gradient(135deg, #BBFFC1 0%, #a8ffae 100%)',
      description: 'Meeting compliance standards'
    },
    {
      title: 'Climate Active',
      value: Math.floor((partnerInfo?.mother_count || 0) * 0.3),
      icon: <VolunteerActivismIcon sx={{ fontSize: 40, opacity: 0.8 }} />,
      color: 'linear-gradient(135deg, #BBFFC1 0%, #a8ffae 100%)',
      description: 'Participating in climate activities'
    },
    {
      title: 'With Donors',
      value: Math.floor((partnerInfo?.mother_count || 0) * 0.52),
      icon: <MonetizationOnIcon sx={{ fontSize: 40, opacity: 0.8 }} />,
      color: 'linear-gradient(135deg, #f92955 0%, #d41e3f 100%)',
      description: 'Receiving donor support'
    }
  ], [partnerInfo]);

  // Loading state
  if (loading && mothers.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        gap: 2
      }}>
        <CircularProgress size={60} sx={{ color: '#f92955' }} />
        <Typography variant="h6" sx={{ color: '#666', textAlign: 'center' }}>
          Loading mothers data...
        </Typography>
        <Typography variant="body2" sx={{ color: '#999', textAlign: 'center' }}>
          Fetching data for partner: {partnerId}
        </Typography>
      </Box>
    );
  }

  // Error state
  if (error && mothers.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Failed to load mothers data
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {error}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Partner ID: {partnerId} | Please check your connection and try again.
          </Typography>
        </Alert>
        <Button 
          variant="contained" 
          onClick={handleRetry}
          sx={{ bgcolor: '#f92955' }}
        >
          Retry Loading Data
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: isMobile ? 1 : 4, bgcolor: '#FFFFFF', minHeight: '100vh' }}>
      {/* Stat Cards */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'row', 
        gap: 2, 
        mb: 4, 
        width: '100%',
        flexWrap: 'wrap'
      }}>
        <Card sx={{ 
          borderRadius: 3, 
          boxShadow: '0 8px 32px rgba(249, 41, 85, 0.12)',
          background: 'linear-gradient(135deg, #f92955 0%, #d41e3f 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          flex: '1 1 250px',
          minWidth: '250px',
          maxWidth: '100%',
        }}>
          <Box sx={{
            position: 'absolute',
            top: -20,
            right: -20,
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
          }} />
          <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 800, fontSize: '2.5rem' }}>
                {partnerInfo?.mother_count || 0}
              </Typography>
              <People sx={{ fontSize: 40, opacity: 0.8 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Total Mothers
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Registered with {partnerInfo?.partner_name}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ 
          borderRadius: 3, 
          boxShadow: '0 8px 32px rgba(187, 255, 193, 0.12)',
          background: 'linear-gradient(135deg, #BBFFC1 0%, #a8ffae 100%)',
          color: 'black',
          position: 'relative',
          overflow: 'hidden',
          flex: '1 1 250px',
          minWidth: '250px',
          maxWidth: '100%',
        }}>
          <Box sx={{
            position: 'absolute',
            top: -20,
            right: -20,
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'rgba(0,0,0,0.1)',
          }} />
          <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 800, fontSize: '2.5rem' }}>
                {mothers.filter(m => m.compliance_score?.includes('/') && 
                  parseInt(m.compliance_score.split('/')[0]) >= parseInt(m.compliance_score.split('/')[1]) * 0.8).length}
              </Typography>
              <EmojiEventsIcon sx={{ fontSize: 40, opacity: 0.8 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Compliant Mothers
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Meeting 80%+ compliance
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ 
          borderRadius: 3, 
          boxShadow: '0 8px 32px rgba(187, 255, 193, 0.12)',
          background: 'linear-gradient(135deg, #BBFFC1 0%, #a8ffae 100%)',
          color: 'black',
          position: 'relative',
          overflow: 'hidden',
          flex: '1 1 250px',
          minWidth: '250px',
          maxWidth: '100%',
        }}>
          <Box sx={{
            position: 'absolute',
            top: -20,
            right: -20,
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'rgba(0,0,0,0.1)',
          }} />
          <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 800, fontSize: '2.5rem' }}>
                {mothers.filter(m => 
                  (m.activities || []).some(a =>
                    climateKeywords.some(kw =>
                      (a.name || '').toLowerCase().includes(kw) || (a.description || '').toLowerCase().includes(kw)
                    )
                  )
                ).length}
              </Typography>
              <VolunteerActivismIcon sx={{ fontSize: 40, opacity: 0.8 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Climate Active
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Participating in climate activities
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ 
          borderRadius: 3, 
          boxShadow: '0 8px 32px rgba(249, 41, 85, 0.12)',
          background: 'linear-gradient(135deg, #f92955 0%, #d41e3f 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          flex: '1 1 250px',
          minWidth: '250px',
          maxWidth: '100%',
        }}>
          <Box sx={{
            position: 'absolute',
            top: -20,
            right: -20,
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
          }} />
          <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 800, fontSize: '2.5rem' }}>
                {mothers.filter(m => m.donor_profile).length}
              </Typography>
              <MonetizationOnIcon sx={{ fontSize: 40, opacity: 0.8 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              With MatchFunding
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Receiving matchfunding support
            </Typography>
          </CardContent>
        </Card>
      </Box>


      {/* Partner Info Header */}
      <Box sx={{ mb: 3, display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', gap: 2 }}>
        {/* Bulk contribution button */}
        {selected.length > 0 && (
          <Button
            variant="contained"
            startIcon={<MonetizationOnIcon />}
            onClick={() => handleContribute(filteredMothers.filter(m => selected.includes(m.generated_id)))}
            sx={{
              bgcolor: '#f92955',
              '&:hover': { bgcolor: '#d41e3f' },
              borderRadius: 2,
              px: 3,
            }}
          >
            Contribute to {selected.length} Mothers
          </Button>
        )}
      </Box>

      {/* Filter bar - always visible */}
      <Paper
        elevation={4}
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: 2,
          px: isMobile ? 2 : 3,
          py: 2,
          borderRadius: 4,
          boxShadow: '0 8px 32px rgba(249, 41, 85, 0.10)',
          minWidth: isMobile ? 'auto' : 600,
          position: isMobile ? 'static' : 'sticky',
          top: isMobile ? undefined : 16,
          zIndex: 10,
          background: 'rgba(255,255,255,0.98)',
          mb: 2,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#f92955', minWidth: 80 }}>
          Filters
        </Typography>
        {/* Location filter */}
        <FormControl sx={{ minWidth: 140 }} size="small">
          <InputLabel>Location</InputLabel>
          <Select
            value={filters.location}
            label="Location"
            onChange={(e) => handleLocationFilterChange(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {locationOptions.map(loc => (
              <MenuItem key={loc} value={loc}>{loc}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* Number of children filter */}
        <FormControl sx={{ minWidth: 120 }} size="small">
          <InputLabel>Children</InputLabel>
          <Select
            value={filters.children}
            label="Children"
            onChange={(e) => handleChildrenFilterChange(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {childrenOptions.map(num => (
              <MenuItem key={num} value={num}>{num}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* Savings progress filter */}
        <Box sx={{ minWidth: 200, px: 1 }}>
          <Typography variant="caption" sx={{ color: '#888', fontWeight: 500 }}>
            Savings (UGX)
          </Typography>
          <Slider
            value={filters.savingsRange}
            min={0}
            max={1000000} // Assuming a max savings value for the slider
            step={1000}
            onChange={(_, val) => handleSavingsRangeChange(val)}
            valueLabelDisplay="auto"
            sx={{ color: '#f92955', mt: -1 }}
            marks={[
              { value: 0, label: 'UGX 0' },
              { value: 1000000, label: 'UGX 1M' },
            ]}
          />
        </Box>
        {/* Climate activity filter */}
        <FormControl size="small">
          <Button
            variant={filters.climateFilter ? 'contained' : 'outlined'}
            color={filters.climateFilter ? 'primary' : 'primary'}
            onClick={() => handleClimateFilterChange(v => !v)}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              px: 2,
              bgcolor: filters.climateFilter ? '#f92955' : undefined,
              color: filters.climateFilter ? '#FFFFFF' : undefined,
              boxShadow: filters.climateFilter ? '0 2px 8px rgba(249, 41, 85, 0.12)' : undefined,
              transition: 'all 0.2s',
            }}
          >
            Climate Activity
          </Button>
        </FormControl>
        {/* Reset button */}
        {(Object.values(filters).some(f => f !== '') || filters.climateFilter) && (
          <Button onClick={handleResetFilters} color="secondary" size="small" sx={{ ml: isMobile ? 0 : 2, fontWeight: 600 }}>
            Reset
          </Button>
        )}
      </Paper>

      {/* Active filter chips */}
      {Object.values(filters).some(f => f !== '') || filters.climateFilter ? (
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
          {filters.location && <Chip label={`Location: ${filters.location}`} onDelete={() => handleLocationFilterChange('')} color="primary" variant="outlined" />}
          {filters.children && <Chip label={`Children: ${filters.children}`} onDelete={() => handleChildrenFilterChange('')} color="primary" variant="outlined" />}
          {filters.savingsRange[0] !== 0 || filters.savingsRange[1] !== 1000000 ? <Chip label={`Savings: UGX ${filters.savingsRange[0].toLocaleString()} - ${filters.savingsRange[1].toLocaleString()}`} onDelete={() => handleSavingsRangeChange([0, 1000000])} color="primary" variant="outlined" /> : null}
          {filters.climateFilter && <Chip label="Climate Activity" onDelete={() => handleClimateFilterChange(false)} color="primary" variant="filled" />}
        </Stack>
      ) : null}

      {/* Table and results */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}
      {!loading && !error && (
        <Paper sx={{ p: 2, borderRadius: 4, boxShadow: '0 8px 32px rgba(249, 41, 85, 0.10)', mt: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>MOTHERS</Typography>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      indeterminate={selected.length > 0 && selected.length < filteredMothers.length}
                      checked={filteredMothers.length > 0 && selected.length === filteredMothers.length}
                      onChange={handleSelectAllClick}
                      inputProps={{ 'aria-label': 'select all mothers' }}
                    />
                  </TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Children</TableCell>
                  <TableCell>Savings (UGX)</TableCell>
                  <TableCell>Monthly Savings</TableCell>
                  <TableCell>Compliance</TableCell>
                  <TableCell>Activities</TableCell>
                  <TableCell>Donor</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedMothers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <Typography variant="body1" sx={{ color: '#888', fontStyle: 'italic', py: 4 }}>
                        No mothers found matching the selected filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedMothers.map((mother) => {
                    const isItemSelected = isSelected(mother.generated_id);
                    return (
                      <TableRow key={mother.generated_id} hover selected={isItemSelected} sx={{ transition: 'background 0.2s' }}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            color="primary"
                            checked={isItemSelected}
                            onChange={event => handleClick(event, mother.generated_id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {mother.first_name} {mother.surname}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              ID: {mother.generated_id}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{mother.mobile_number}</Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {mother.location}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{mother.num_children}</Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            Ages: {(Array.isArray(mother.ages_of_children) ? mother.ages_of_children.join(', ') : mother.ages_of_children)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                            {mother.savings.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: 'info.main' }}>
                            {mother.total_monthly_savings.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ 
                            color: mother.compliance_score.includes('/') && 
                            parseInt(mother.compliance_score.split('/')[0]) >= parseInt(mother.compliance_score.split('/')[1]) * 0.8 
                              ? 'success.main' 
                              : 'warning.main' 
                          }}>
                            {mother.compliance_score}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {mother.activities?.length || 0} activities
                          </Typography>
                          {/* Climate badge */}
                          {(mother.activities || []).some(a =>
                            climateKeywords.some(kw =>
                              (a.name || '').toLowerCase().includes(kw) || (a.description || '').toLowerCase().includes(kw)
                            )
                          ) && (
                            <Chip label="Climate" size="small" color="success" sx={{ mt: 0.5 }} />
                          )}
                        </TableCell>
                        <TableCell>
                          {mother.donor_profile ? (
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {mother.donor_profile.first_name} {mother.donor_profile.surname}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {mother.donor_profile.country_of_residence}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                              No donor
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <IconButton color="primary" onClick={() => handleOpenDrawer(mother)}>
                              <VisibilityIcon />
                            </IconButton>
                            <IconButton 
                              color="success" 
                              onClick={() => handleContribute(mother)}
                              sx={{ bgcolor: 'success.light', color: 'white', '&:hover': { bgcolor: 'success.main' } }}
                            >
                              <MonetizationOnIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredMothers.length}
            rowsPerPage={pagination.rowsPerPage}
            page={pagination.page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ border: 0, mt: 2 }}
          />
        </Paper>
      )}

      {/* Mother Profile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: {
            width: isMobile ? '100%' : 440,
            maxWidth: '100vw',
            height: '100vh',
            background: 'linear-gradient(135deg, #f92955 0%, #f5f5f5 100%)',
            boxShadow: '-10px 0 30px rgba(249, 41, 85, 0.18)',
            borderTopLeftRadius: 32,
            borderBottomLeftRadius: 32,
            p: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }
        }}
      >
        {selectedMother && (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Gradient Header with Avatar */}
            <Box
              sx={{
                background: 'linear-gradient(120deg, #f92955 0%, #ffe4ec 100%)',
                p: 0,
                pb: 4,
                position: 'relative',
                borderBottomLeftRadius: 48,
                boxShadow: '0 8px 32px rgba(249, 41, 85, 0.10)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <IconButton
                onClick={handleCloseDrawer}
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  color: '#fff',
                  bgcolor: 'rgba(0,0,0,0.08)',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.18)' },
                  zIndex: 2,
                }}
              >
                <CloseIcon />
              </IconButton>
              <Avatar
                src={selectedMother.avatar || '/woman-avatar.png'}
                sx={{
                  width: 120,
                  height: 120,
                  mt: 5,
                  mb: 1,
                  border: '6px solid #fff',
                  boxShadow: '0 8px 24px rgba(249, 41, 85, 0.12)',
                  background: '#fff',
                }}
              />
              <Typography variant="h5" sx={{ color: '#fff', fontWeight: 'bold', letterSpacing: 1, mt: 1, textShadow: '0 2px 8px #f92955' }}>
                {selectedMother.first_name} {selectedMother.surname}
              </Typography>
              <Typography variant="body2" sx={{ color: '#fff', opacity: 0.85, fontStyle: 'italic', mb: 1 }}>
                {selectedMother.location}
              </Typography>
              {/* Contribute button */}
              <Button
                variant="contained"
                startIcon={<MonetizationOnIcon />}
                onClick={() => handleContribute(selectedMother)}
                sx={{
                  mt: 2,
                  bgcolor: '#fff',
                  color: '#f92955',
                  fontWeight: 'bold',
                  '&:hover': { bgcolor: '#f5f5f5' },
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}
              >
                Contribute to Education
              </Button>
            </Box>
            {/* Profile Content */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 3, background: 'rgba(255,255,255,0.95)' }}>
              {/* Funding Gap */}
              <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 3, background: 'rgba(255,245,250,0.85)', border: '2px solid #f92955' }}>
                <Typography variant="h6" sx={{ color: '#f92955', fontWeight: 'bold', mb: 1 }}>
                  Education Funding Gap
                </Typography>
                <Typography variant="h4" sx={{ color: '#d41e3f', fontWeight: 'bold' }}>
                  USD {calculateFundingGap(selectedMother)}
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>
                  Annual education cost needed for {selectedMother.num_children} children
                </Typography>
              </Paper>

              {/* Contact & Basic Info */}
              <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 3, background: 'rgba(255,245,250,0.85)' }}>
                <Stack direction="row" spacing={2} alignItems="center" mb={1}>
                  <PhoneIcon color="primary" />
                  <Typography variant="body1" sx={{ fontWeight: 600 }}> {selectedMother.mobile_number || 'N/A'} </Typography>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center" mb={1}>
                  <LocationOnIcon color="secondary" />
                  <Typography variant="body1"> {selectedMother.location || 'N/A'} </Typography>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center" mb={1}>
                  <ChildCareIcon color="success" />
                  <Typography variant="body1"> {selectedMother.num_children || 0} children </Typography>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center" mb={1}>
                  <SchoolIcon color="info" />
                  <Typography variant="body1"> {selectedMother.education_level || 'N/A'} </Typography>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center" mb={1}>
                  <AccountCircleIcon color="action" />
                  <Typography variant="body1"> NIN: {selectedMother.nin || 'N/A'} </Typography>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center" mb={1}>
                  <CalendarMonthIcon color="action" />
                  <Typography variant="body1"> Created: {selectedMother.created_at ? new Date(selectedMother.created_at).toLocaleDateString() : 'N/A'} </Typography>
                </Stack>
              </Paper>
              {/* Savings & Scores */}
              <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 3, background: 'rgba(245,255,250,0.85)' }}>
                <Stack direction="row" spacing={2} alignItems="center" mb={1}>
                  <SavingsIcon color="primary" />
                  <Typography variant="body1"> Total Savings: <b>UGX {selectedMother.savings?.toLocaleString() || 0}</b> </Typography>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center" mb={1}>
                  <SavingsIcon color="secondary" />
                  <Typography variant="body1"> Monthly Savings: <b>UGX {selectedMother.total_monthly_savings?.toLocaleString() || 0}</b> </Typography>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center" mb={1}>
                  <EmojiEventsIcon color="warning" />
                  <Typography variant="body1"> Milestone Score: <b>{selectedMother.milestone_score || 0}</b> </Typography>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center" mb={1}>
                  <VolunteerActivismIcon color="success" />
                  <Typography variant="body1"> Compliance: <b>{selectedMother.compliance_score || 'N/A'}</b> </Typography>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center" mb={1}>
                  <SavingsIcon color="info" />
                  <Typography variant="body1"> Donor Contributions: <b>UGX {selectedMother.donor_contributions?.toLocaleString() || 0}</b> </Typography>
                </Stack>
              </Paper>
              {/* Donor Profile */}
              <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 3, background: 'rgba(245,245,255,0.85)' }}>
                <Typography variant="subtitle2" sx={{ color: '#888', mb: 1 }}>Donor Profile</Typography>
                {selectedMother.donor_profile ? (
                  <Box sx={{ pl: 1, mb: 1 }}>
                    <Typography variant="body2" sx={{ color: '#BBFFC1' }}><strong>Name:</strong> {selectedMother.donor_profile.first_name} {selectedMother.donor_profile.surname}</Typography>
                    <Typography variant="body2" sx={{ color: '#BBFFC1' }}><strong>Email:</strong> {selectedMother.donor_profile.email}</Typography>
                    <Typography variant="body2" sx={{ color: '#BBFFC1' }}><strong>Country:</strong> {selectedMother.donor_profile.country_of_residence || 'N/A'}</Typography>
                    <Typography variant="body2" sx={{ color: '#BBFFC1' }}><strong>Total Contributions:</strong> UGX {selectedMother.donor_profile.total_contributions?.toLocaleString() || 0}</Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: '#777' }}>No donor assigned</Typography>
                )}
              </Paper>
              {/* Activities */}
              <Paper elevation={0} sx={{ p: 2, borderRadius: 3, background: 'rgba(255,255,245,0.85)' }}>
                <Typography variant="subtitle2" sx={{ color: '#888', mb: 1 }}>Activities</Typography>
                {selectedMother.activities && selectedMother.activities.length > 0 ? (
                  selectedMother.activities.map((activity) => (
                    <Box key={activity.activity_id} sx={{ pl: 1, mb: 1, borderLeft: '3px solid #f92955' }}>
                      <Typography variant="body2" sx={{ color: '#BBFFC1', fontWeight: 600 }}>{activity.name}</Typography>
                      <Typography variant="caption" sx={{ color: '#777' }}>{activity.description}</Typography>
                      <Typography variant="caption" sx={{ color: '#777', display: 'block' }}>Participants: {activity.num_people}</Typography>
                      <Typography variant="caption" sx={{ color: '#777', display: 'block' }}>Created: {activity.created_at ? new Date(activity.created_at).toLocaleDateString() : 'N/A'}</Typography>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" sx={{ color: '#777' }}>No activities recorded</Typography>
                )}
              </Paper>
            </Box>
          </Box>
        )}
      </Drawer>

      {/* Contribution Modal */}
      <Dialog
        open={contributionOpen}
        onClose={handleContributionClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: '#f92955', 
          color: '#fff', 
          fontWeight: 'bold',
          borderTopLeftRadius: 3,
          borderTopRightRadius: 3,
        }}>
          {contributionStep === 1 && 'Contribution Details'}
          {contributionStep === 2 && 'Contribution Summary'}
          {contributionStep === 3 && 'Payment Processing'}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {contributionStep === 1 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2, color: '#333' }}>
                Contribute to {contributionData.mothers.length} Mother{contributionData.mothers.length > 1 ? 's' : ''}
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Contribution Amount (USD)</InputLabel>
                    <Select
                      value={contributionData.amount}
                      label="Contribution Amount (USD)"
                      onChange={(e) => setContributionData(prev => ({ ...prev, amount: e.target.value }))}
                    >
                      <MenuItem value={30}>$30 (Recommended)</MenuItem>
                      <MenuItem value={50}>$50</MenuItem>
                      <MenuItem value={100}>$100</MenuItem>
                      <MenuItem value={200}>$200</MenuItem>
                      <MenuItem value={500}>$500</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <FormLabel>Contribution Frequency</FormLabel>
                    <RadioGroup
                      value={contributionData.frequency}
                      onChange={(e) => setContributionData(prev => ({ ...prev, frequency: e.target.value }))}
                    >
                      <FormControlLabel value="monthly" control={<Radio />} label="Monthly" />
                      <FormControlLabel value="quarterly" control={<Radio />} label="Quarterly" />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                    Selected Mothers:
                  </Typography>
                  <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                    {contributionData.mothers.map((mother) => (
                      <Chip
                        key={mother.generated_id}
                        label={`${mother.first_name} ${mother.surname} - ${mother.location}`}
                        sx={{ m: 0.5 }}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
          
          {contributionStep === 2 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 3, color: '#333' }}>
                Contribution Summary
              </Typography>
              
              <Card sx={{ mb: 2, p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Contribution Details
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Amount per mother:</strong> ${contributionData.amount}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Frequency:</strong> {contributionData.frequency}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Number of mothers:</strong> {contributionData.mothers.length}
                </Typography>
                <Typography variant="h6" sx={{ color: '#f92955', fontWeight: 'bold', mt: 2 }}>
                  Total: ${calculateTotalAmount()}
                </Typography>
              </Card>
              
              <Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic' }}>
                This contribution will help cover education costs for the selected mothers' children.
              </Typography>
            </Box>
          )}
          
          {contributionStep === 3 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 3, color: '#333' }}>
                Payment Method
              </Typography>
              
              {paymentMethods.length === 0 ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No payment methods found. Please add a payment method in Settings first.
                </Alert>
              ) : (
                <FormControl fullWidth>
                  <InputLabel>Select Payment Method</InputLabel>
                  <Select
                    value={contributionData.paymentMethod}
                    label="Select Payment Method"
                    onChange={(e) => setContributionData(prev => ({ ...prev, paymentMethod: e.target.value }))}
                  >
                    {paymentMethods.map((method) => (
                      <MenuItem key={method.id} value={method.id}>
                        {method.cardType} - {method.cardNumber} {method.isDefault && '(Default)'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              
              <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ color: '#f92955', fontWeight: 'bold' }}>
                  Final Amount: ${calculateTotalAmount()}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          {contributionStep > 1 && (
            <Button onClick={handleContributionBack} sx={{ color: '#666' }}>
              Back
            </Button>
          )}
          <Button onClick={handleContributionClose} sx={{ color: '#666' }}>
            Cancel
          </Button>
          {contributionStep < 3 ? (
            <Button 
              onClick={handleContributionNext} 
              variant="contained"
              sx={{ 
                bgcolor: '#f92955',
                '&:hover': { bgcolor: '#d41e3f' },
              }}
            >
              Next
            </Button>
          ) : (
            <Button 
              onClick={handleContributionConfirm} 
              variant="contained"
              disabled={!contributionData.paymentMethod}
              sx={{ 
                bgcolor: '#f92955',
                '&:hover': { bgcolor: '#d41e3f' },
              }}
            >
              Confirm Payment
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default Mothers;